import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'

// ============================================================
// useMusicTracks — Custom hook for audio track management
//
// Mirrors the same shape as usePhotos but for audio files.
// Storage bucket : 'Music'
// Database table : 'music_tracks'
//
// Columns expected in music_tracks:
//   id          uuid  primary key
//   user_id     uuid  references auth.users
//   name        text  display name shown in the UI
//   file_path   text  path inside the 'Music' bucket
//   tags        text[] array of tag strings
//   uploaded_at timestamptz
//
// Key difference from usePhotos:
//   Tracks have tags[] but NO category field.
//   The file path includes a human-readable sanitized name
//   so downloaded files have a sensible filename.
// ============================================================

export function useMusicTracks() {

  const [tracks, setTracks] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError]   = useState(null)

  // Fetch tracks once when the hook mounts
  useEffect(() => {
    fetchTracks()
  }, [])


  // -------------------------------------------------------
  // fetchTracks
  // Loads all tracks for the current user, sorted newest
  // first.  After fetching rows we resolve each file_path
  // into a public URL so the rest of the app can use
  // track.url directly without extra calls.
  // -------------------------------------------------------
  const fetchTracks = async () => {
    try {
      setLoading(true)
      setError(null)

      const { data: { user }, error: userError } = await supabase.auth.getUser()
      if (userError) throw userError
      if (!user) throw new Error('Not authenticated')

      const { data, error: fetchError } = await supabase
        .from('music_tracks')
        .select('*')
        .eq('user_id', user.id)
        .order('uploaded_at', { ascending: false })

      if (fetchError) throw fetchError

      // Attach a public URL to each track row so the player
      // and email-share features can access the audio directly
      const tracksWithUrls = await Promise.all(
        (data || []).map(async (track) => {
          const { data: urlData } = supabase.storage
            .from('Music')
            .getPublicUrl(track.file_path)

          return {
            ...track,
            url: urlData.publicUrl
          }
        })
      )

      setTracks(tracksWithUrls)

    } catch (err) {
      setError(err.message)
      console.error('Error fetching tracks:', err)
    } finally {
      setLoading(false)
    }
  }


  // -------------------------------------------------------
  // uploadTrack
  // Uploads an audio file to Supabase Storage then saves
  // metadata to the music_tracks table.
  //
  // FILE NAMING
  // Files are stored as:
  //   userId/Beyonce_Megamix_k2m9p.mp3
  //
  // Readable name comes FIRST, short random suffix at the END
  // to prevent collisions when two tracks share the same name.
  // No timestamp prefix — storage filenames stay clean and
  // human-readable in the Supabase dashboard.
  //
  // The sendTracks email additionally appends ?download= to
  // the URL so the DJ's browser saves the file as
  // PerformerName_TrackName.mp3 regardless of the suffix.
  // -------------------------------------------------------
  const uploadTrack = async (file, metadata) => {

    try {
      setError(null)

      const { data: { user }, error: userError } = await supabase.auth.getUser()
      if (userError) throw userError
      if (!user) throw new Error('Not authenticated')

      // ---- BUILD READABLE FILENAME ----

      const fileExt = file.name.split('.').pop().toLowerCase()
      // → 'mp3', 'wav', 'flac', etc.

      const sanitizedName = metadata.name
        .trim()
        // strip leading/trailing spaces first
        .replace(/[^a-zA-Z0-9\s\-_]/g, '')
        // remove special characters that are unsafe in file paths
        // apostrophes, accented letters, emoji, etc. all get stripped
        // safe chars kept: letters, digits, spaces, hyphens, underscores
        .replace(/\s+/g, '_')
        // collapse multiple spaces and convert to underscores
        // "Beyoncé  Megamix" → "Beyonc_Megamix" (accent stripped above)
        .substring(0, 60)
        // cap at 60 chars so storage paths stay reasonable length
        || 'track'
        // fallback: if the entire name was special characters and
        // got wiped out, use 'track' so the upload doesn't fail

      const uniqueSuffix = Math.random().toString(36).substring(2, 7)
      // generates a short 5-char random string: "k2m9p"
      // appended at the END so the readable name comes first
      // 36^5 = ~60 million combinations — collision risk is negligible

      const fileName = `${sanitizedName}_${uniqueSuffix}.${fileExt}`
      // Full result example: "Beyonce_Megamix_k2m9p.mp3"
      // Clean name first, tiny unique suffix at the end to prevent
      // collisions if two tracks share the same display name

      const filePath = `${user.id}/${fileName}`
      // Scoped under the user's own folder for RLS compatibility

      // ---- STEP 1: UPLOAD AUDIO FILE TO STORAGE ----
      const { error: uploadError } = await supabase.storage
        .from('Music')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
          // upsert: false → fail loudly if path already exists
          // (short random suffix makes collisions essentially impossible)
          contentType: file.type,
          // passing the MIME type (audio/mpeg, audio/wav, etc.) so
          // Supabase sets the correct Content-Type header on the object
          // which in turn helps browsers handle playback vs download
        })

      if (uploadError) throw uploadError

      // ---- STEP 2: SAVE METADATA ROW TO DATABASE ----
      // We store the display name separately from the file path
      // so renaming a track in the UI doesn't require moving
      // the storage object.
      const { data: metaData, error: metaError } = await supabase
        .from('music_tracks')
        .insert([{
          user_id:     user.id,
          name:        metadata.name,
          // store the original display name — not the sanitized one —
          // so the UI always shows the exact name the user typed
          file_path:   filePath,
          tags:        metadata.tags || [],
          uploaded_at: new Date().toISOString()
        }])
        .select()

      if (metaError) throw metaError
      if (!metaData || metaData.length === 0) throw new Error('Failed to save track metadata')

      // ---- STEP 3: RESOLVE PUBLIC URL AND UPDATE STATE ----
      const { data: urlData } = supabase.storage
        .from('Music')
        .getPublicUrl(filePath)

      const newTrack = {
        ...metaData[0],
        url: urlData.publicUrl
      }

      // Prepend to list so the new track appears at the top immediately
      setTracks(prev => [newTrack, ...prev])

      return newTrack

    } catch (err) {
      setError(err.message)
      console.error('Error uploading track:', err)
      throw err
      // re-throw so the calling component can show its own error UI
    }
  }


  // -------------------------------------------------------
  // deleteTrack
  // Removes the file from Storage first, then deletes the
  // database row.  Order matters: if the DB delete fails
  // after a successful storage delete, the row becomes an
  // orphan pointing at a missing file.  Doing storage first
  // is the lesser evil — an orphan row is easier to clean up
  // than a ghost file with no row.
  // -------------------------------------------------------
  const deleteTrack = async (trackId, filePath) => {
    try {
      setError(null)

      // Remove the audio file from the Music bucket
      const { error: storageError } = await supabase.storage
        .from('Music')
        .remove([filePath])

      if (storageError) throw storageError

      // Then delete the metadata row
      const { error: dbError } = await supabase
        .from('music_tracks')
        .delete()
        .eq('id', trackId)

      if (dbError) throw dbError

      // Remove from local state so the UI updates instantly
      setTracks(prev => prev.filter(t => t.id !== trackId))

    } catch (err) {
      setError(err.message)
      console.error('Error deleting track:', err)
      throw err
    }
  }


  // -------------------------------------------------------
  // updateTrack
  // Updates metadata fields (name, tags) in the DB.
  // Does NOT move the storage file — if the user renames a
  // track the file_path keeps the original sanitized name.
  // The display name (track.name) is what everyone sees, so
  // that's the only field that needs to change here.
  // -------------------------------------------------------
  const updateTrack = async (trackId, updates) => {
    try {
      setError(null)

      const { data, error: updateError } = await supabase
        .from('music_tracks')
        .update(updates)
        .eq('id', trackId)
        .select()

      if (updateError) throw updateError

      // Re-resolve public URL in case file_path was changed
      const { data: urlData } = supabase.storage
        .from('Music')
        .getPublicUrl(data[0].file_path)

      const updatedTrack = {
        ...data[0],
        url: urlData.publicUrl
      }

      // Swap the updated track into the existing list
      setTracks(prev => prev.map(t => t.id === trackId ? updatedTrack : t))

      return updatedTrack

    } catch (err) {
      setError(err.message)
      console.error('Error updating track:', err)
      throw err
    }
  }


  return {
    tracks,       // Track[]  — array of track objects with .url attached
    loading,      // boolean  — true while fetchTracks is running
    error,        // string|null — last error message, null if clean
    uploadTrack,  // (file: File, metadata: { name, tags }) => Promise<Track>
    deleteTrack,  // (trackId: string, filePath: string) => Promise<void>
    updateTrack,  // (trackId: string, updates: object) => Promise<Track>
    fetchTracks   // () => Promise<void> — call to manually refresh
  }
}